/**
 * Off-Label Use of Human Drugs consent form — interactive form for
 * populating the treatment name before generating the PDF.
 */
import { Input } from '@/frontend/components/ui/input'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { OwnerDetailsPreview } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignaturePreview } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetailsPreview } from '@/frontend/features/printing/documents/blocks/patient-details'
import {
  defaultOffLicenceConsentFields,
  OFF_LICENCE_ACKNOWLEDGEMENTS,
  OFF_LICENCE_ACKNOWLEDGEMENTS_INTRO,
  OFF_LICENCE_CONSENT_TEXT,
  OFF_LICENCE_DECLINE_TEXT,
  OFF_LICENCE_DISCUSSION_TEXT,
  OFF_LICENCE_REASONS,
  OFF_LICENCE_RISKS_INTRO,
  OFF_LICENCE_SIGNING_TEXT,
  type OffLicenceConsentFields,
  OffLicenceConsentPdf,
} from '@/frontend/features/printing/documents/off-licence-consent.print'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function OffLicenceConsentForm({
  context,
  value,
  onChange,
}: DocumentFormProps<OffLicenceConsentFields>) {
  const { patient, owner, clinic } = context

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-4 text-center text-lg font-bold underline">
        CONSENT FORM FOR OFF-LABEL USE OF HUMAN DRUGS IN PETS
      </h1>

      <OwnerDetailsPreview owner={owner} />
      <div className="mt-3">
        <PatientDetailsPreview patient={patient} weight={context.weight} showDob showMicrochip />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <strong>Consent for treatment with</strong>
        <Input
          className="w-64"
          value={value.treatmentName}
          onChange={(e) => onChange({ ...value, treatmentName: e.target.value })}
          placeholder="Drug / treatment name"
        />
      </div>

      <p className="mt-4">{OFF_LICENCE_CONSENT_TEXT}</p>
      <ul className="mt-2 list-disc pl-6">
        {OFF_LICENCE_REASONS.map((reason) => (
          <li key={reason.slice(0, 30)}>{reason}</li>
        ))}
      </ul>

      <p className="mt-4">{OFF_LICENCE_RISKS_INTRO}</p>

      <p className="mt-4">{OFF_LICENCE_ACKNOWLEDGEMENTS_INTRO}</p>
      <ul className="mt-2 list-disc pl-6">
        {OFF_LICENCE_ACKNOWLEDGEMENTS.map((ack) => (
          <li key={ack.slice(0, 30)}>{ack}</li>
        ))}
      </ul>

      <p className="mt-4">{OFF_LICENCE_DISCUSSION_TEXT}</p>
      <p className="mt-4">{OFF_LICENCE_DECLINE_TEXT}</p>
      <p className="mt-4 font-semibold">{OFF_LICENCE_SIGNING_TEXT}</p>

      <OwnerSignaturePreview owner={context.owner} />
    </DocumentPreview>
  )
}

export const offLicenceConsent = withDocumentEditor({
  id: 'off-licence-consent',
  label: 'Off-Label Use Consent',
  defaultFields: defaultOffLicenceConsentFields,
  Preview: OffLicenceConsentForm,
  Pdf: OffLicenceConsentPdf,
})
